import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormContainer } from "react-hook-form-mui";
import { APIContext } from "../../../../Contexts/APIContext";
import { SessionContext } from "../../../../Contexts/SessionContext";
import ValidatedCheckBoxElement from "../../../../Utilities/Forms/ValidatedCheckBoxElement";
import DropDownViewModel from "../../../../Utilities/ViewModels/DropDownViewModel";
import MonitoringReportViewModel from "../../../ViewModels/TravelPlan/MonitoringReportViewModel";
import TripGeneration from "./TripGeneration";
import UserDefinedModeSplit from "./UserDefinedModeSplit";

interface IProps {
    parameterDropdown: DropDownViewModel[] | [];
    surveyTimesDropdown: DropDownViewModel[] | [];
    surveyEndTimesDropdown: DropDownViewModel[] | [];
    onShowResults: () => void;
    modeDropdown: DropDownViewModel[] | [];
    reference: string;

}

interface SurveyOption {
    reference: string;
    label: string;
    isSelectedSite: boolean;
}

export default function MonitoringReportSelectionParameters({ parameterDropdown, surveyTimesDropdown, surveyEndTimesDropdown, onShowResults, modeDropdown, reference }: IProps) {
    const { get } = useContext(APIContext);
    const { sessionState, updateSessionState } = useContext(SessionContext);
    const getRef = useRef(get);
    getRef.current = get;

    const formContext = useForm<MonitoringReportViewModel>({
        defaultValues: sessionState.monitoringReport?.siteReference === reference
            ? sessionState.monitoringReport
            : new MonitoringReportViewModel(),
    });

    const [surveyOptions, setSurveyOptions] = useState<SurveyOption[]>([]);

    useEffect(() => {
        getRef.current("Site", `GetMonitoringReportSurveyOptions?reference=${reference}`, (options: { reference: string; latestSurveyDate?: string | null; isSelectedSite: boolean }[]) => {
            const nextOptions = options.map(option => ({
                reference: option.reference,
                label: option.latestSurveyDate
                    ? `${option.reference} ${option.isSelectedSite ? "(site selected)" : "(Earlier)"} (${option.latestSurveyDate})`
                    : `${option.reference} ${option.isSelectedSite ? "(site selected)" : "(Earlier)"}`,
                isSelectedSite: option.isSelectedSite,
            }));

            setSurveyOptions(nextOptions);

            if (nextOptions.length > 0 && !(formContext.getValues("includedSurveys")?.length)) {
                formContext.setValue("includedSurveys", [nextOptions[0].reference]);
            }
        });
    }, [reference]);

    const includedSurveys = formContext.watch("includedSurveys") ?? [];

    const toggleSurveySelection = (surveyReference: string, checked: boolean) => {
        const currentSelections = formContext.getValues("includedSurveys") ?? [];
        const nextSelections = checked
            ? Array.from(new Set([...currentSelections, surveyReference]))
            : currentSelections.filter((selectedSurvey) => selectedSurvey !== surveyReference);

        formContext.setValue("includedSurveys", nextSelections);
    };

    const submit = () => {
        const values = formContext.getValues();
        if (!values.includedSurveys || values.includedSurveys.length === 0) {
            values.includedSurveys = surveyOptions.length > 0 ? [surveyOptions[0].reference] : [];
        }

        updateSessionState({
            monitoringReport: {
                ...values,
                siteReference: reference,
            },
        });
        onShowResults();
    }


    return (
        <Grid container size={12}>
            <FormContainer formContext={formContext}>
                <Grid sx={{ pt: 2 }}>
                    <Button variant="outlined" color="primary"  onClick={() => submit()} fullWidth={false}>Calculate Results</Button>
                </Grid>
                {surveyOptions.length > 0 && (
                    <Grid size={12}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6">Survey Selection</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Select the surveys to include in the monitoring report results. The latest survey is selected by default.
                            </Typography>
                            <Stack>
                                {surveyOptions.map((surveyOption) => (
                                    <FormControlLabel
                                        key={surveyOption.reference}
                                        label={surveyOption.label}
                                        control={
                                            <Checkbox
                                                checked={includedSurveys.includes(surveyOption.reference)}
                                                onChange={(event) => toggleSurveySelection(surveyOption.reference, event.target.checked)}
                                            />
                                        }
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                )}
                <Grid size={12}>
                    <ValidatedCheckBoxElement name={`modeSplitTotal`} label={`Mode Split Total`} />
                    <Typography variant="body2">Select to display a modal split pie chart covering the duration of the surveys(s) at this site, which splits the method of transport for all journeys (arrivals plus departures) between vehicle occupants, public transport users, pedestrians cyclists and scooters.</Typography>
                    <Typography variant="caption">All Time Periods. All Modes, Arrivals + Departures</Typography>
                </Grid>


                <Grid size={12}>
                    <ValidatedCheckBoxElement name={`vehicleOccupancySplitGraph`} label={`Vehicle Occupancy Split Graph`} />
                    <Typography variant="body2">You can also choose to display a bar chart that displays the number of vehicles containing 1 occupant, 2 occupants, 3 occupants etc.</Typography>
                    <Typography variant="caption">All Time Periods. All Modes, Arrivals + Departures</Typography>
                </Grid>

                <Grid size={12}>
                    <Typography variant="h6">User Defined Mode Split</Typography>
                    <Typography variant="body2"> <Typography variant="body2">Create up to two further modal split pie charts by defining your own survey start and end times, along with specifying the direction of transport flow in each case.</Typography></Typography>
                </Grid>
                <Grid size={12} key={1}>
                    <UserDefinedModeSplit index={1} baseName={`userDefinedModeSplits1`} formContext={formContext} surveyTimesDropdown={surveyTimesDropdown} surveyEndTimesDropdown={surveyEndTimesDropdown} />
                </Grid>

                <Grid size={12} key={2}>
                    <UserDefinedModeSplit index={2} baseName={`userDefinedModeSplits2`} formContext={formContext} surveyTimesDropdown={surveyTimesDropdown} surveyEndTimesDropdown={surveyEndTimesDropdown} />
                </Grid>

                <Grid size={12} key={3}>
                    <UserDefinedModeSplit index={3} baseName={`userDefinedModeSplits3`} formContext={formContext} surveyTimesDropdown={surveyTimesDropdown} surveyEndTimesDropdown={surveyEndTimesDropdown} />
                </Grid>

                <Grid size={12}>
                    <Typography variant="h6">Trip Generation</Typography>
                    <Typography variant="body2">Please make selections that will display a trip generation rate based on the survey(s) at this site. Select your start and end times, your method of transport, and your direction of transport flow in each case (you can undertake up to three calculations).</Typography>
                </Grid>
                <Grid size={12}>
                    <TripGeneration index={1} baseName={`tripGenerations1`} formContext={formContext} timeOptions={surveyTimesDropdown} endTimeOptions={surveyEndTimesDropdown} tripRateParamOptions={parameterDropdown} modeOptions={modeDropdown} />
                </Grid>
                <Grid size={12}>
                    <TripGeneration index={2} baseName={`tripGenerations2`} formContext={formContext} timeOptions={surveyTimesDropdown} endTimeOptions={surveyEndTimesDropdown} tripRateParamOptions={parameterDropdown} modeOptions={modeDropdown} />
                </Grid>
                <Grid size={12}>
                    <TripGeneration index={3} baseName={`tripGenerations3`} formContext={formContext} timeOptions={surveyTimesDropdown} endTimeOptions={surveyEndTimesDropdown} tripRateParamOptions={parameterDropdown} modeOptions={modeDropdown} />
                </Grid>

            </FormContainer>
        </Grid>
    );
}
